
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { agent_capability_type } from '@/types/agentic';
import { 
  hasCapabilityPermission, 
  hasCapabilityTypePermission, 
  CapabilityPermissionLevel
} from '@/services/agentic/permissionService';

interface PermissionState {
  loading: boolean;
  hasPermission: boolean;
  checkPermission: () => Promise<boolean>;
}

/**
 * Custom hook to check capability permissions
 */
export const useCapabilityPermission = (
  capabilityId: string,
  permissionLevel: CapabilityPermissionLevel
): PermissionState => {
  const [loading, setLoading] = useState<boolean>(true);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const { user } = useAuth();
  
  const checkPermission = async () => {
    setLoading(true);
    try {
      if (!user) {
        setHasPermission(false);
        return false;
      }
      
      const permission = await hasCapabilityPermission(capabilityId, permissionLevel);
      setHasPermission(permission);
      return permission;
    } catch (error) {
      console.error('Error checking capability permission:', error);
      setHasPermission(false);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (user) {
      checkPermission();
    } else {
      setHasPermission(false);
      setLoading(false);
    }
  }, [capabilityId, permissionLevel, user]);
  
  return { loading, hasPermission, checkPermission };
};

/**
 * Custom hook to check capability type permissions
 */
export const useCapabilityTypePermission = (
  capabilityType: agent_capability_type,
  permissionLevel: CapabilityPermissionLevel
): PermissionState => {
  const [loading, setLoading] = useState<boolean>(true);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const { user } = useAuth();
  
  const checkPermission = async () => {
    setLoading(true);
    try {
      if (!user) {
        setHasPermission(false);
        return false;
      }
      
      const permission = await hasCapabilityTypePermission(capabilityType, permissionLevel);
      setHasPermission(permission);
      return permission;
    } catch (error) {
      console.error('Error checking capability type permission:', error);
      setHasPermission(false);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (user) {
      checkPermission();
    } else {
      setHasPermission(false);
      setLoading(false);
    }
  }, [capabilityType, permissionLevel, user]);
  
  return { loading, hasPermission, checkPermission };
};
